import { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useGym } from '../../contexts/GymContext';
import MyGymsTab from './MyGymsTab';
import FindGymsTab from './FindGymsTab';

const TABS = {
  MY_GYMS: 'my-gyms',
  FIND_GYMS: 'find-gyms'
};

const GymSelectorModal = ({ isOpen, onClose }) => {
  const {
    userGyms,
    selectedGym,
    recentGyms,
    selectGym,
    selectGymOnce,
    addGym,
    setPrimaryGym,
    removeGym
  } = useGym();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(TABS.MY_GYMS);
  const searchInputRef = useRef(null);
  const modalRef = useRef(null);
  const startYRef = useRef(0);
  const currentYRef = useRef(0);

  // Determine default tab based on user state
  useEffect(() => {
    if (isOpen) {
      const defaultTab = getDefaultTab();
      setActiveTab(defaultTab);
      setSearchQuery('');
    }
  }, [isOpen]);

  const getDefaultTab = () => {
    if (userGyms.length > 0) return TABS.MY_GYMS; // Has gyms, show them
    return TABS.FIND_GYMS; // New user, guide them to add gyms
  };

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case '1':
          if (!e.target.matches('input')) {
            setActiveTab(TABS.MY_GYMS);
          }
          break;
        case '2':
          if (!e.target.matches('input')) {
            setActiveTab(TABS.FIND_GYMS);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleSelectGym = (gym) => {
    selectGym(gym);
    handleClose();
  };

  const handleBookOnce = (gym) => {
    selectGymOnce(gym);
    handleClose();
  };

  const handleAddGym = async (gymId) => {
    const result = await addGym(gymId);
    if (result.success) {
      // Optionally switch to My Gyms tab to show the added gym
      // or stay on Find Gyms for continued browsing
    }
    return result;
  };

  const handleSetPrimary = async (gym) => {
    // Find the userGym ID from the gym
    const userGym = userGyms.find(g => g.gymId === gym.gymId);
    if (userGym?.id) {
      await setPrimaryGym(userGym.id);
    }
  };

  const handleRemoveGym = async (gym) => {
    const userGym = userGyms.find(g => g.gymId === gym.gymId);
    if (userGym?.id) {
      await removeGym(userGym.id);
    }
  };

  // Pull-to-close gesture handlers for mobile
  const handleTouchStart = (e) => {
    startYRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    currentYRef.current = e.touches[0].clientY;
    const diff = currentYRef.current - startYRef.current;

    // Only allow pulling down when at top of scroll
    if (diff > 0 && modalRef.current?.scrollTop === 0) {
      e.preventDefault();
      if (diff < 150) {
        modalRef.current.style.transform = `translateY(${diff}px)`;
      }
    }
  };

  const handleTouchEnd = () => {
    const diff = currentYRef.current - startYRef.current;

    if (diff > 100) {
      handleClose();
    }

    // Reset transform
    if (modalRef.current) {
      modalRef.current.style.transform = '';
    }
  };

  // Get favorite gyms (most recent)
  const getFavoriteGyms = () => {
    return recentGyms
      .map(gymId => userGyms.find(g => g.gymId === gymId))
      .filter(Boolean)
      .slice(0, 5);
  };

  const favoriteGyms = getFavoriteGyms();

  // Get search placeholder based on active tab
  const getSearchPlaceholder = () => {
    switch (activeTab) {
      case TABS.MY_GYMS:
        return 'Search your gyms...';
      case TABS.FIND_GYMS:
        return 'Search all gyms by name or location...';
      default:
        return 'Search gyms...';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      {/* Mobile: Bottom sheet | Desktop: Centered modal */}
      <div
        ref={modalRef}
        className="bg-white w-full sm:max-w-lg sm:mx-4 rounded-t-2xl sm:rounded-2xl sm:border-2 sm:border-slate-900 sm:shadow-retro max-h-[85vh] flex flex-col transition-transform duration-200"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'none' }}
      >
        {/* Pull indicator (mobile only) */}
        <div className="sm:hidden pt-2 pb-1 flex justify-center">
          <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4 pb-3 border-b-2 border-slate-900">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
            Select Gym
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-teal-50 rounded-lg transition-colors touch-manipulation border-2 border-transparent hover:border-teal-200"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Tab Bar */}
        <div className="flex border-b-2 border-slate-900 bg-white">
          <button
            onClick={() => setActiveTab(TABS.MY_GYMS)}
            className={`flex-1 px-4 py-3 font-bold text-sm transition-all touch-manipulation ${
              activeTab === TABS.MY_GYMS
                ? 'text-teal-600 border-b-4 border-teal-500 bg-teal-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 border-b-4 border-transparent'
            }`}
          >
            My Gyms
          </button>
          <button
            onClick={() => setActiveTab(TABS.FIND_GYMS)}
            className={`flex-1 px-4 py-3 font-bold text-sm transition-all touch-manipulation ${
              activeTab === TABS.FIND_GYMS
                ? 'text-teal-600 border-b-4 border-teal-500 bg-teal-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 border-b-4 border-transparent'
            }`}
          >
            Find Gyms
          </button>
        </div>

        {/* Search Bar */}
        <div className="sticky top-0 bg-white p-4 pb-3 border-b border-gray-100 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={getSearchPlaceholder()}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
            />
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {activeTab === TABS.MY_GYMS && (
            <MyGymsTab
              userGyms={userGyms}
              selectedGym={selectedGym}
              onSelectGym={handleSelectGym}
              onSetPrimary={handleSetPrimary}
              onRemoveGym={handleRemoveGym}
              searchQuery={searchQuery}
              favoriteGyms={favoriteGyms}
            />
          )}

          {activeTab === TABS.FIND_GYMS && (
            <FindGymsTab
              userGyms={userGyms}
              searchQuery={searchQuery}
              onBookOnce={handleBookOnce}
              onAddGym={handleAddGym}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GymSelectorModal;
